import { create } from 'zustand';
import { Intent, Plan, Column, DataType } from '../types';
import { aiApi } from '../services/api';
import { useWorkflowStore, FlowNode, FlowEdge } from './workflowStore';
import { useUploadedFileStore, UploadedFileData } from './uploadedFileStore';

// 生成唯一节点 ID
let nodeIdCounter = 0;
function generateNodeId(type: string): string {
  nodeIdCounter += 1;
  return `node_${type}_${Date.now()}_${nodeIdCounter}`;
}

// 重置计数器
export function resetNodeIdCounter() {
  nodeIdCounter = 0;
}

const STEP_LABELS: Record<string, string> = {
  load_excel: '加载数据',
  load_csv: '加载数据',
  filter: '筛选数据',
  group_by: '分组',
  aggregate: '聚合统计',
  sort: '排序数据',
  join: '合并数据',
  export_excel: '导出结果',
  export_csv: '导出结果',
};

// 推断列的数据类型
function inferColumnType(columnName: string, data: Record<string, unknown>[]): DataType {
  const values = data.map(r => r[columnName]).filter(v => v !== null && v !== undefined && v !== '');
  if (values.length === 0) return 'string';
  
  // 检查是否都是数字
  if (values.every(v => typeof v === 'number' || !isNaN(Number(v)))) return 'number';
  
  // 检查是否都是日期
  if (values.every(v => /^\d{4}[-/]\d{2}[-/]\d{2}/.test(String(v)))) return 'date';
  
  return 'string';
}

// 从 plan.steps 构建节点（后端已生成完整流程）
function buildNodesFromSteps(plan: Plan, baseY = 100): { nodes: FlowNode[]; edges: FlowEdge[] } {
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];
  const steps = plan.steps || [];
  if (steps.length === 0) return { nodes, edges };

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const label = STEP_LABELS[step.action] || step.action;
    const params = step.params || {};
    const nodeId = step.id || generateNodeId(step.action);
    const node: FlowNode = {
      id: nodeId,
      type: step.action,
      position: { x: 100, y: baseY + i * 100 },
      data: {
        label,
        config: { type: step.action, params },
        status: 'idle',
      },
    };
    nodes.push(node);
    for (const dep of step.depends || []) {
      edges.push({ id: `e_${dep}_${nodeId}`, source: dep, target: nodeId });
    }
  }
  return { nodes, edges };
}

// 从计划生成工作流节点
function generateNodesFromPlan(plan: Plan, existingNodes: FlowNode[] = [], existingEdges: FlowEdge[] = []): { nodes: FlowNode[]; edges: FlowEdge[] } {
  // 优先使用后端 plan.steps 构建（确保参数正确）
  const steps = plan.steps || [];
  if (steps.length > 0 && existingNodes.length === 0) {
    return buildNodesFromSteps(plan);
  }

  // 修改模式：在现有节点基础上添加
  const nodes: FlowNode[] = [...existingNodes];
  const edges: FlowEdge[] = [...existingEdges];
  let lastNodeId: string | null = null;
  let currentY = 100;

  if (nodes.length > 0) {
    const maxYNode = nodes.reduce((max, node) => node.position.y > max.position.y ? node : max, nodes[0]);
    lastNodeId = maxYNode.id;
    currentY = maxYNode.position.y + 100;
    const exportNodes = nodes.filter(n => n.type === 'export_excel' || n.type === 'export_csv');
    exportNodes.forEach(exportNode => {
      const edgeIndex = edges.findIndex(e => e.target === exportNode.id);
      if (edgeIndex !== -1) {
        lastNodeId = edges[edgeIndex].source;
        edges.splice(edgeIndex, 1);
      }
    });
  } else {
    const loadNode: FlowNode = {
      id: generateNodeId('load'),
      type: 'load_excel',
      position: { x: 100, y: currentY },
      data: {
        label: '加载数据',
        config: {
          type: 'load_excel',
          params: { table: plan.intent?.tables?.[0] || 'data' },
        },
        status: 'idle',
      },
    };
    nodes.push(loadNode);
    lastNodeId = loadNode.id;
    currentY += 100;
  }

  const intentType = plan.intent?.type || 'filter';

  switch (intentType) {
    case 'filter':
      const filterNode: FlowNode = {
        id: generateNodeId('filter'),
        type: 'filter',
        position: { x: 100, y: currentY },
        data: {
          label: '筛选数据',
          config: {
            type: 'filter',
            params: { 
              conditions: plan.intent?.conditions || [],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(filterNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${filterNode.id}`, source: lastNodeId, target: filterNode.id });
      }
      lastNodeId = filterNode.id;
      currentY += 100;
      break;
      
    case 'aggregate':
      const aggNode: FlowNode = {
        id: generateNodeId('aggregate'),
        type: 'aggregate',
        position: { x: 100, y: currentY },
        data: {
          label: '聚合统计',
          config: {
            type: 'aggregate',
            params: {
              groupBy: plan.intent?.columns || [],
              aggregations: [{ column: 'amount', function: 'sum' }],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(aggNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${aggNode.id}`, source: lastNodeId, target: aggNode.id });
      }
      lastNodeId = aggNode.id;
      currentY += 100;
      break;
      
    case 'sort':
      const sortNode: FlowNode = {
        id: generateNodeId('sort'),
        type: 'sort',
        position: { x: 100, y: currentY },
        data: {
          label: '排序数据',
          config: {
            type: 'sort',
            params: {
              fields: (plan.intent?.columns || []).map((col: string) => ({
                column: col,
                order: 'asc',
              })),
            },
          },
          status: 'idle',
        },
      };
      nodes.push(sortNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${sortNode.id}`, source: lastNodeId, target: sortNode.id });
      }
      lastNodeId = sortNode.id;
      currentY += 100;
      break;
      
    case 'join':
      const joinNode: FlowNode = {
        id: generateNodeId('join'),
        type: 'join',
        position: { x: 100, y: currentY },
        data: {
          label: '合并数据',
          config: {
            type: 'join',
            params: {
              joinType: 'left',
              keyColumns: plan.intent?.columns || [],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(joinNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${joinNode.id}`, source: lastNodeId, target: joinNode.id });
      }
      lastNodeId = joinNode.id;
      currentY += 100;
      break;

    case 'dedup':
      const dedupNode: FlowNode = {
        id: generateNodeId('dedup'),
        type: 'dedup',
        position: { x: 100, y: currentY },
        data: {
          label: '去除重复',
          config: {
            type: 'dedup',
            params: {
              columns: plan.intent?.columns || [],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(dedupNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${dedupNode.id}`, source: lastNodeId, target: dedupNode.id });
      }
      lastNodeId = dedupNode.id;
      currentY += 100;
      break;

    case 'compute':
      const computeNode: FlowNode = {
        id: generateNodeId('compute'),
        type: 'compute',
        position: { x: 100, y: currentY },
        data: {
          label: '计算列',
          config: {
            type: 'compute',
            params: {
              expressions: plan.intent?.entities?.expressions || [],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(computeNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${computeNode.id}`, source: lastNodeId, target: computeNode.id });
      }
      lastNodeId = computeNode.id;
      currentY += 100;
      break;

    case 'group_by':
      const groupByNode: FlowNode = {
        id: generateNodeId('group_by'),
        type: 'group_by',
        position: { x: 100, y: currentY },
        data: {
          label: '分组',
          config: {
            type: 'group_by',
            params: {
              groupBy: plan.intent?.columns || [],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(groupByNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${groupByNode.id}`, source: lastNodeId, target: groupByNode.id });
      }
      lastNodeId = groupByNode.id;
      currentY += 100;
      break;
  }
  
  // 如果没有导出节点，添加一个
  const hasExportNode = nodes.some(n => n.type === 'export_excel' || n.type === 'export_csv');
  if (!hasExportNode && lastNodeId) {
    const exportNode: FlowNode = {
      id: generateNodeId('export'),
      type: 'export_excel',
      position: { x: 100, y: currentY },
      data: {
        label: '导出结果',
        config: {
          type: 'export_excel',
          params: {},
        },
        status: 'idle',
      },
    };
    nodes.push(exportNode);
    edges.push({ id: `e_${lastNodeId}_${exportNode.id}`, source: lastNodeId, target: exportNode.id });
  } else if (hasExportNode && lastNodeId) {
    // 重新连接导出节点到最后一个处理节点
    const exportNode = nodes.find(n => n.type === 'export_excel' || n.type === 'export_csv')!;
    edges.push({ id: `e_${lastNodeId}_${exportNode.id}`, source: lastNodeId, target: exportNode.id });
  }
  
  return { nodes, edges };
}

interface AIState {
  // 当前意图
  currentIntent: Intent | null;
  
  // 当前计划
  currentPlan: Plan | null;
  
  // 生成的 Workflow JSON
  generatedWorkflow: string | null;
  
  // 生成的节点和边
  generatedNodes: FlowNode[];
  generatedEdges: FlowEdge[];
  
  // 加载状态
  isParsing: boolean;
  isGenerating: boolean;
  
  // 错误信息
  error: string | null;
  
  // 操作
  setCurrentIntent: (intent: Intent | null) => void;
  setCurrentPlan: (plan: Plan | null) => void;
  setGeneratedWorkflow: (workflow: string | null) => void;
  parseIntent: (input: string, context?: { availableTables: { name: string; columns: { name: string; type: string }[] }[] }) => Promise<void>;
  generatePlan: (intent: Intent, tables?: { name: string; columns: { name: string; type: string }[] }[]) => Promise<void>;
  confirmPlan: (planId: string, confirm: boolean, feedback?: string) => Promise<void>;
  applyGeneratedWorkflow: (uploadedFile?: UploadedFileData | null) => void;
  modifyWorkflow: (input: string, context?: { availableTables: { name: string; columns: { name: string; type: string }[] }[] }, uploadedFile?: UploadedFileData | null) => Promise<void>;
  clear: () => void;
  reset: () => void;
}

export const useAIStore = create<AIState>()((set, get) => ({
  currentIntent: null,
  currentPlan: null,
  generatedWorkflow: null,
  generatedNodes: [],
  generatedEdges: [],
  isParsing: false,
  isGenerating: false,
  error: null,

  setCurrentIntent: (intent) => set({ currentIntent: intent }),
  
  setCurrentPlan: (plan) => set({ currentPlan: plan }),
  
  setGeneratedWorkflow: (workflow) => set({ generatedWorkflow: workflow }),

  parseIntent: async (input, context) => {
    set({ isParsing: true, error: null });
    try {
      const response = await aiApi.parseIntent(input, context);
      if (response.code === 0) {
        const plan = response.data.plan || null;
        
        // 生成节点和边
        let nodes: FlowNode[] = [];
        let edges: FlowEdge[] = [];
        
        if (plan) {
          const generated = generateNodesFromPlan(plan);
          nodes = generated.nodes;
          edges = generated.edges;
        }
        
        set({
          currentIntent: response.data.intent,
          currentPlan: plan,
          generatedWorkflow: response.data.workflow || null,
          generatedNodes: nodes,
          generatedEdges: edges,
        });
      } else {
        set({ error: response.message });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isParsing: false });
    }
  },

  generatePlan: async (intent, tables) => {
    set({ isGenerating: true, error: null });
    try {
      const response = await aiApi.generatePlan(intent, tables);
      if (response.code === 0) {
        const plan = response.data.plan;
        
        // 生成节点和边
        let nodes: FlowNode[] = [];
        let edges: FlowEdge[] = [];
        
        if (plan) {
          const generated = generateNodesFromPlan(plan);
          nodes = generated.nodes;
          edges = generated.edges;
        }
        
        set({
          currentPlan: plan,
          generatedWorkflow: response.data.workflow,
          generatedNodes: nodes,
          generatedEdges: edges,
        });
      } else {
        set({ error: response.message });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isGenerating: false });
    }
  },

  confirmPlan: async (planId, confirm, feedback) => {
    try {
      const response = await aiApi.confirmPlan(planId, confirm, feedback);
      if (response.code === 0) {
        // 处理确认结果
        if (confirm && response.data.workflowId) {
          // 创建成功，可以跳转到工作流编辑器
        }
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    }
  },
  
  // 应用生成的工作流到 workflow store
  applyGeneratedWorkflow: (uploadedFile?: UploadedFileData | null) => {
    const { generatedNodes, generatedEdges } = get();
    if (generatedNodes.length > 0) {
      const workflowStore = useWorkflowStore.getState();
      const uploadedFileStore = useUploadedFileStore.getState();
      
      // 清空现有工作流
      workflowStore.clearWorkflow();
      
      // 处理生成的节点，如果有上传文件则关联到加载节点
      const processedNodes = generatedNodes.map(node => {
        // 如果是加载节点且有上传文件，关联文件信息
        if ((node.type === 'load_excel' || node.type === 'load_csv') && uploadedFile) {
          // 将文件关联到此节点
          uploadedFileStore.setFileForNode(node.id, uploadedFile);
          
          // 更新节点配置，包含文件信息
          const columns: Column[] = uploadedFile.columns.map((name, idx) => ({
            name,
            type: inferColumnType(name, uploadedFile.data),
            nullable: uploadedFile.data.some((r: Record<string, unknown>) => r[name] === null || r[name] === undefined || r[name] === ''),
            index: idx,
          }));
          
          return {
            ...node,
            data: {
              ...node.data,
              config: {
                type: node.type,
                params: {
                  ...node.data.config.params,
                  fileName: uploadedFile.name,
                  rowCount: uploadedFile.rowCount,
                  table: node.data.config.params.table || uploadedFile.name.replace(/\.[^/.]+$/, ''),
                  columns: columns,
                },
              },
            },
          };
        }
        return node;
      });
      
      // 添加处理后的节点和边
      processedNodes.forEach(node => {
        workflowStore.addNode(node);
      });
      
      generatedEdges.forEach(edge => {
        workflowStore.addEdge(edge);
      });
      
      // 更新生成的节点状态
      set({ generatedNodes: processedNodes });
    }
  },

  // 修改现有工作流 - 在现有节点基础上添加新节点
  modifyWorkflow: async (input, context) => {
    set({ isParsing: true, error: null });
    try {
      const response = await aiApi.parseIntent(input, context);
      if (response.code === 0) {
        const plan = response.data.plan || null;
        
        if (plan) {
          // 在现有节点基础上添加新节点
          const { generatedNodes, generatedEdges } = get();
          const generated = generateNodesFromPlan(plan, generatedNodes, generatedEdges);
          
          set({
            currentIntent: response.data.intent,
            currentPlan: plan,
            generatedWorkflow: response.data.workflow || null,
            generatedNodes: generated.nodes,
            generatedEdges: generated.edges,
          });
        }
      } else {
        set({ error: response.message });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isParsing: false });
    }
  },

  clear: () =>
    set({
      currentIntent: null,
      currentPlan: null,
      generatedWorkflow: null,
      error: null,
    }),

  reset: () => {
    resetNodeIdCounter();
    set({
      currentIntent: null,
      currentPlan: null,
      generatedWorkflow: null,
      generatedNodes: [],
      generatedEdges: [],
      error: null,
    });
  },
}));
